import { ActionResponse, DataTableConfig, DomainTableConfig, DomainType, UIFeedbackHooks } from "@/Shared/Types";
import { ModalHook } from "./TechnicalHooks";
import { DOMAIN_CONFIGS } from "@/Shared";
import { useAppNavigation } from "./useAppNavigation";
import { useEffect, useMemo, useRef } from "react";
import { useAppSelector, usePermission } from "@/OneLiners";
import { useAccessRight } from "@/Domains";
import { ActionStatusHelper } from "@/Shared/Helpers";
import { useSortDomain } from "./useSortDomain";


export function useDomainLayout(
    domainType: DomainType,
    uiFeedbackHooks: UIFeedbackHooks = {} as UIFeedbackHooks,
    inModalHook?: ModalHook,
    extraArgs?: {
        entityId?: number
    }
): {
    tableConfig: DataTableConfig<any>,
    sortedTableConfig: DataTableConfig<any>,
    response: ActionResponse
} {
    const selfBadges = useAppSelector(state => state.uiState.badges)?.[domainType];
    const sortData = useSortDomain<any>(domainType);

    /** Resolve domain-specific detail configuration */
    const { useTableConfig, DomainIcon } = DOMAIN_CONFIGS[domainType];

    const { goToErrorPage } = useAppNavigation();

    const permissionHook = usePermission();

    const accessRight = useAccessRight(permissionHook.dataMap);


    /** Instantiate the domain-specific detail hook with UI feedback */
    const { tableConfig, response: tableHookResponse } = useTableConfig?.({
        accessRight,
        uiFeedbackHooks,
        permissionHook,
        inModalHook,
        extraArgs: { ...extraArgs, selfBadges }
    }
    ) as DomainTableConfig<any>;

    const response = {
        status: ActionStatusHelper.combine(
            tableHookResponse.status,
            permissionHook.stateResponse.status,
            //accessRight.status
        ),
        error: tableHookResponse.error ??
           permissionHook.stateResponse.error
    }

    /**
     * Computes the widget configuration:
     * - Uses full data set if `allowAll` is enabled
     * - Otherwise limits and sorts the most recent items
     *
     * Memoized to avoid unnecessary recalculations
     */
    const sortedTableConfig = useMemo(() => {
        if (!tableConfig) return undefined;
        return {
            ...tableConfig,
            initialData: sortData(tableConfig.initialData ?? []),
            Icon: DomainIcon
        }
    },
        [tableConfig]) as DataTableConfig<any>;

    /*
    /** Prepare table configuration based on screen size *
    const getTableConfig = (): DataTableConfig<any> => {
        const data = { ...tableConfig, icon: domainIcon };
        return isDesktop ? data : { ...data, initialData: sortData(data.initialData ?? []) }
    }*/

    /**
     * Guard to prevent multiple redirects during async state transitions
     */
    const hasRedirected = useRef(false);


    useEffect(() => {

        if (!accessRight?.hasEnums || hasRedirected.current) return;

        if (ActionStatusHelper.isSucceeded(response.status) && !accessRight.canRead(domainType)) {
            hasRedirected.current = true;
            goToErrorPage(403);
        }
    }, [accessRight, domainType, response.status]);


    return {
        tableConfig,
        response,
        sortedTableConfig
    };
}